import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { taskAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiArrowLeft, FiEdit2, FiTrash2, FiPlus, FiSend, FiX, FiSave,
  FiCheck, FiClock, FiUser, FiCalendar, FiActivity, FiLink, FiRepeat, FiFolder,
  FiChevronDown, FiChevronRight, FiFileText
} from 'react-icons/fi';

const PCOL = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };
const PBG  = { low:'#dcfce7', medium:'#fef3c7', high:'#fee2e2', critical:'#ede9fe' };
const SCOL = {
  todo:        { bg:'#f1f5f9', color:'#475569' },
  'in-progress':{ bg:'#fef3c7', color:'#92400e' },
  review:      { bg:'#e0e7ff', color:'#3730a3' },
  completed:   { bg:'#d1fae5', color:'#065f46' },
  blocked:     { bg:'#fee2e2', color:'#991b1b' },
};
const ACT_ICONS = { created:'🆕', commented:'💬', status_changed:'🔄', assigned:'👤', checklist_updated:'✅' };

export default function TaskDetail() {
  const { id } = useParams(); const navigate = useNavigate(); const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';
  const isClient = user?.role === 'client';

  const [task,setTask]         = useState(null);
  const [users,setUsers]       = useState([]);
  const [allTasks,setAllTasks] = useState([]);
  const [loading,setLoading]   = useState(true);
  const [editing,setEditing]   = useState(false);
  const [editForm,setEditForm] = useState({});
  const [comment,setComment]   = useState('');
  const [tab,setTab]           = useState('details');
  const [newCheck,setNewCheck]           = useState('');
  const [expandedCheck,setExpandedCheck] = useState(null);   // item._id expanded
  const [checkEdits,setCheckEdits]       = useState({});     // { [itemId]: { text, notes } }
  const [savingCheck,setSavingCheck]     = useState(null);   // item._id being saved
  const [deleteModal,setDeleteModal]     = useState(false);
  const [depModal,setDepModal]           = useState(false);
  const [assignDropdown,setAssignDropdown] = useState(null); // item._id with open dropdown
  const [saving,setSaving]           = useState(false);
  const [commenting,setCommenting]   = useState(false);
  const [timeModal,setTimeModal]     = useState(null); // { iid, done } — pending complete
  const [timeTaken,setTimeTaken]     = useState('');

  const load = async () => {
    try {
      const [tR, uR] = await Promise.all([
        taskAPI.getById(id),
        userAPI.getAll(),
      ]);
      const t = tR.data.task || tR.data;
      setTask(t);
      setEditForm({
        ...t,
        assignedTo: Array.isArray(t.assignedTo) ? t.assignedTo.map(u=>u._id||u) : t.assignedTo ? [t.assignedTo._id||t.assignedTo] : [],
        dueDate: t.dueDate ? t.dueDate.slice(0,10) : '',
      });
      setUsers(uR.data.users || []);
    } catch { toast.error('Failed to load task'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const loadDeps = async () => {
    if (allTasks.length) return;
    try {
      const r = await taskAPI.getAll({ project: task?.project?._id || task?.project });
      setAllTasks((r.data.tasks || []).filter(t => t._id !== id));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to load tasks'); }
  };
  const save = async () => {
    setSaving(true);
    try { await taskAPI.update(id, editForm); toast.success('Task updated'); setEditing(false); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Update failed'); }
    setSaving(false);
  };
  const deleteTask = async () => {
    try { await taskAPI.delete(id); navigate('/tasks'); }
    catch { toast.error('Failed to delete'); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    setCommenting(true);
    try { await taskAPI.addComment(id, { text:comment }); setComment(''); load(); }
    catch { toast.error('Failed to send'); }
    setCommenting(false);
  };
  const toggleCheck = (iid, done) => {
    if (!done) {
      // marking complete — ask for time taken
      setTimeTaken('');
      setTimeModal({ iid, done });
    } else {
      // unchecking — no time prompt needed
      submitToggle(iid, done, null);
    }
  };

  const submitToggle = async (iid, done, minutes) => {
    try {
      await taskAPI.updateChecklist(id, iid, {
        completed: !done,
        ...(minutes ? { timeTakenMinutes: parseInt(minutes) } : {}),
      });
      load();
    }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to update checklist'); }
    setTimeModal(null);
    setTimeTaken('');
  };

  const toggleExpand = (item) => {
    if (expandedCheck === item._id) {
      setExpandedCheck(null);
    } else {
      setExpandedCheck(item._id);
      // seed edit state with current values if not already set
      if (!checkEdits[item._id]) {
        setCheckEdits(prev => ({ ...prev, [item._id]: {
          text:         item.text,
          notes:        item.notes || '',
          assignedToId: item.assignedTo?._id || item.assignedTo?.id || item.assignedToId || null,
        }}));
      }
    }
  };

  const saveCheckDetails = async (iid) => {
    const edits = checkEdits[iid];
    if (!edits) return;
    setSavingCheck(iid);
    try {
      await taskAPI.updateChecklist(id, iid, {
        text:         edits.text,
        notes:        edits.notes,
        assignedToId: edits.assignedToId ? parseInt(edits.assignedToId) : null,
      });
      load();
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
    setSavingCheck(null);
  };
  const deleteCheck = async (e, iid) => {
    e.stopPropagation();
    try { await taskAPI.deleteChecklist(id, iid); load(); }
    catch { toast.error('Failed to delete item'); }
  };
  const addCheck = async () => {
    if (!newCheck.trim()) return;
    try { await taskAPI.addChecklist(id, { text:newCheck }); setNewCheck(''); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to add item'); }
  };
  const quickAssign = async (itemId, userId) => {
    setAssignDropdown(null);
    try { await taskAPI.updateChecklist(id, itemId, { assignedToId: userId || null }); load(); }
    catch { toast.error('Failed to assign'); }
  };
  useEffect(() => {
    if (!assignDropdown) return;
    const close = () => setAssignDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [assignDropdown]);
  const toggleAssignee = (uid) => {
    const cur = editForm.assignedTo || [];
    setEditForm({ ...editForm, assignedTo: cur.includes(uid) ? cur.filter(x=>x!==uid) : [...cur, uid] });
  };
  const addDep = async (depId) => {
    const deps = [...(task.dependencies||[]).map(d=>d._id||d), depId];
    try { await taskAPI.update(id, { dependencies:deps }); setDepModal(false); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to add dependency'); }
  };
  const removeDep = async (depId) => {
    const deps = (task.dependencies||[]).map(d=>d._id||d).filter(d=>d!==depId);
    try { await taskAPI.update(id, { dependencies:deps }); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to remove dependency'); }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (!task) return (
    <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
      <p>Task not found</p>
      <Link to="/tasks" style={{ color:'var(--pm-primary)' }}>← Back to Tasks</Link>
    </div>
  );

  const sc        = SCOL[task.status] || SCOL.todo;
  const assignees = Array.isArray(task.assignedTo) ? task.assignedTo : task.assignedTo ? [task.assignedTo] : [];
  const isAssigned= assignees.some(a => (a._id||a) === user?._id);
  const canEdit   = isAdmin || (user?.role === 'team' && isAssigned);
  const checkDone = (task.checklist||[]).filter(c => c.completed).length;
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

  const S = {
    page: { maxWidth:1100, margin:'0 auto', fontFamily:"'Sora',sans-serif" },
    card: { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9', marginBottom:20 },
    btn:  (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff', boxShadow:'0 4px 14px rgba(99,102,241,.2)' }
        : v==='danger'  ? { background:'#fee2e2', color:'#ef4444' }
        :                 { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' }) }),
    inp:  { width:'100%', padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', boxSizing:'border-box', marginTop:6, fontFamily:"'Sora',sans-serif" },
    tab:  (a) => ({ padding:'9px 20px', borderRadius:9, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', transition:'all .2s', whiteSpace:'nowrap',
      background: a ? '#fff' : 'transparent', color: a ? 'var(--pm-primary)' : '#64748b', boxShadow: a ? '0 1px 6px #0000001a' : 'none' }),
    ov:   { position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 },
    mo:   { background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:480, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 24px 64px #00000022' },
    badge:(bg,color) => ({ background:bg, color, padding:'4px 14px', borderRadius:20, fontSize:12, fontWeight:700, textTransform:'uppercase', display:'inline-flex', alignItems:'center', gap:4 }),
  };

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        @keyframes spin { to { transform:rotate(360deg) } }
        .dep-row:hover { background:#f0f4ff!important; border-color:#6366f1!important; }
        .check-item:hover { background:#f0f4ff; }
        .tab-btn:hover { color: var(--pm-primary)!important; }
        .check-accordion-open .check-item:hover { background:transparent; }
      `}</style>

      {/* ── Top navigation bar ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <Link to="/tasks" style={{ display:'inline-flex', alignItems:'center', gap:7, color:'#64748b', textDecoration:'none', fontWeight:600, fontSize:14, padding:'8px 14px', borderRadius:9, background:'#f8fafc', border:'1.5px solid #e2e8f0', transition:'all .2s' }}
          onMouseEnter={e=>e.currentTarget.style.borderColor='var(--pm-primary)'}
          onMouseLeave={e=>e.currentTarget.style.borderColor='#e2e8f0'}>
          <FiArrowLeft size={14}/> All Tasks
        </Link>
        <div style={{ display:'flex', gap:8 }}>
          {canEdit && (editing ? <>
            <button style={S.btn('ghost')} onClick={() => setEditing(false)} disabled={saving}><FiX size={13}/> Cancel</button>
            <button style={{ ...S.btn('primary'), opacity: saving ? .7 : 1 }} onClick={save} disabled={saving}>
              <FiSave size={13}/> {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </> : <>
            <button style={S.btn('ghost')} onClick={() => setEditing(true)}><FiEdit2 size={13}/> Edit</button>
            {isAdmin && <button style={S.btn('danger')} onClick={() => setDeleteModal(true)}><FiTrash2 size={13}/></button>}
          </>)}
        </div>
      </div>

      {/* ── Hero card ── */}
      <div style={{ background:'linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#1a1f3a 100%)', borderRadius:20, padding:'28px 32px', marginBottom:20, position:'relative', overflow:'hidden' }}>
        {/* subtle bg accent */}
        <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'rgba(99,102,241,.12)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:160, height:160, borderRadius:'50%', background:'rgba(139,92,246,.08)', pointerEvents:'none' }}/>

        {/* Project breadcrumb */}
        {task.project && (
          <Link to={`/projects/${task.project._id||task.project}`}
            style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'#818cf8', textDecoration:'none', fontWeight:600, marginBottom:12, opacity:.9 }}>
            <FiFolder size={11}/> {task.project.name || 'View Project'} →
          </Link>
        )}

        {/* Title */}
        {editing
          ? <input style={{ background:'rgba(255,255,255,.08)', border:'1.5px solid rgba(255,255,255,.15)', color:'#f8fafc', borderRadius:10, padding:'10px 14px', fontSize:22, fontWeight:800, width:'100%', outline:'none', boxSizing:'border-box', marginBottom:16 }}
              value={editForm.title || ''} onChange={e => setEditForm({ ...editForm, title:e.target.value })} placeholder="Task title"/>
          : <h1 style={{ margin:'0 0 16px', fontSize:26, fontWeight:800, color:'#f8fafc', lineHeight:1.25, position:'relative' }}>{task.title}</h1>
        }

        {/* Badges row */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:18, position:'relative' }}>
          {editing ? <>
            <select style={{ padding:'5px 12px', borderRadius:20, border:'none', fontWeight:700, fontSize:12, background: sc.bg, color: sc.color, cursor:'pointer' }}
              value={editForm.status} onChange={e => setEditForm({ ...editForm, status:e.target.value })}>
              {['todo','in-progress','review','completed','blocked'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select style={{ padding:'5px 12px', borderRadius:20, border:'none', fontWeight:700, fontSize:12, background: PBG[editForm.priority]||'#f1f5f9', color: PCOL[editForm.priority]||'#94a3b8', cursor:'pointer' }}
              value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority:e.target.value })}>
              {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </> : <>
            <span style={S.badge(sc.bg, sc.color)}>{task.status.replace('-',' ')}</span>
            <span style={S.badge(PBG[task.priority]||'#f1f5f9', PCOL[task.priority]||'#94a3b8')}>
              <span style={{ width:6, height:6, borderRadius:'50%', background: PCOL[task.priority]||'#94a3b8', display:'inline-block' }}/>
              {task.priority}
            </span>
            {isOverdue && <span style={S.badge('#fee2e244','#ef4444')}>⚠ Overdue</span>}
            {task.recurrence?.enabled && <span style={S.badge('#ede9fe','#7c3aed')}><FiRepeat size={10}/> {task.recurrence.frequency}</span>}
          </>}
        </div>

        {/* Meta row */}
        <div style={{ display:'flex', gap:20, flexWrap:'wrap', position:'relative' }}>
          {assignees.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FiUser size={13} color="#64748b"/>
              <div style={{ display:'flex', gap:4 }}>
                {assignees.map((a, i) => {
                  const name = typeof a === 'object' ? a.name : users.find(u => u._id === a)?.name || 'User';
                  return (
                    <span key={i} style={{ background:'rgba(255,255,255,.12)', color:'#e2e8f0', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>
                      {name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {task.dueDate && (
            <div style={{ display:'flex', alignItems:'center', gap:6, color: isOverdue ? '#f87171' : '#94a3b8', fontSize:13 }}>
              <FiCalendar size={13}/>
              {new Date(task.dueDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
            </div>
          )}
          {task.estimatedHours > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, color:'#94a3b8', fontSize:13 }}>
              <FiClock size={13}/> {task.estimatedHours}h estimated
            </div>
          )}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display:'flex', gap:4, background:'#f1f5f9', borderRadius:12, padding:5, marginBottom:20, overflowX:'auto' }}>
        {[
          ['details',      'Details'],
          ['checklist',    `Checklist ${(task.checklist||[]).length ? `(${checkDone}/${(task.checklist||[]).length})` : ''}`],
          ['comments',     `Comments ${(task.comments||[]).length ? `(${(task.comments||[]).length})` : ''}`],
          ['dependencies', `Dependencies ${(task.dependencies||[]).length ? `(${(task.dependencies||[]).length})` : ''}`],
          ['activity',     'Activity'],
        ].map(([v, l]) => (
          <button key={v} className="tab-btn" style={S.tab(tab === v)} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {/* ══ DETAILS TAB ══ */}
      {tab === 'details' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {/* Description card */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:4, height:18, background:'var(--pm-gradient)', borderRadius:2, display:'inline-block' }}/>
              Description
            </h3>
            {editing
              ? <textarea style={{ ...S.inp, resize:'vertical', minHeight:100 }} value={editForm.description||''} onChange={e => setEditForm({ ...editForm, description:e.target.value })} rows={4} placeholder="Describe this task…"/>
              : <p style={{ color:'#475569', lineHeight:1.8, margin:0, fontSize:14 }}>{task.description || <span style={{ color:'#94a3b8', fontStyle:'italic' }}>No description provided.</span>}</p>
            }
            {editing && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:16 }}>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569' }}>
                  Due Date
                  <input type="date" style={S.inp} value={editForm.dueDate||''} onChange={e => setEditForm({ ...editForm, dueDate:e.target.value })}/>
                </label>
                <label style={{ fontSize:13, fontWeight:600, color:'#475569' }}>
                  Est. Hours
                  <input type="number" style={S.inp} value={editForm.estimatedHours||''} onChange={e => setEditForm({ ...editForm, estimatedHours:e.target.value })} placeholder="0"/>
                </label>
              </div>
            )}
          </div>

          {/* Assignees card */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:4, height:18, background:'var(--pm-gradient)', borderRadius:2, display:'inline-block' }}/>
              {editing ? 'Assign Members' : 'Assigned To'}
            </h3>
            {editing ? (
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto', marginBottom:16 }}>
                {users.filter(u => u.role !== 'client').map(u => {
                  const sel = (editForm.assignedTo||[]).includes(u._id);
                  return (
                    <div key={u._id} onClick={() => toggleAssignee(u._id)}
                      style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, cursor:'pointer',
                        background: sel ? '#eff6ff' : '#f8fafc', border:`1.5px solid ${sel ? 'var(--pm-primary)' : 'transparent'}`, transition:'all .15s' }}>
                      <div style={{ width:30, height:30, borderRadius:'50%', background: sel ? 'var(--pm-gradient)' : '#e2e8f0', color: sel ? '#fff' : '#64748b',
                        display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0 }}>{u.name?.charAt(0)}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:'#0f172a' }}>{u.name}</div>
                        <div style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize' }}>{u.role}</div>
                      </div>
                      {sel && <FiCheck size={14} color="var(--pm-primary)"/>}
                    </div>
                  );
                })}
              </div>
            ) : (
              assignees.length === 0
                ? <div style={{ textAlign:'center', padding:'20px 0', color:'#94a3b8', fontSize:14 }}>
                    <FiUser size={24} style={{ marginBottom:8, opacity:.3 }}/><br/>Unassigned
                  </div>
                : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {assignees.map((a, i) => {
                      const u = typeof a === 'object' ? a : users.find(x => x._id === (a._id||a));
                      return u ? (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'#f8fafc', borderRadius:10 }}>
                          <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff',
                            display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, flexShrink:0 }}>{u.name?.charAt(0)}</div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{u.name}</div>
                            <div style={{ fontSize:12, color:'#94a3b8', textTransform:'capitalize' }}>{u.role}</div>
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
            )}
            {editing && (
              <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #f1f5f9' }}>
                <h4 style={{ margin:'0 0 10px', fontSize:13, fontWeight:700, color:'#475569', display:'flex', alignItems:'center', gap:6 }}>
                  <FiRepeat size={12}/> Recurrence
                </h4>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', marginBottom:8 }}>
                  <input type="checkbox" checked={editForm.recurrence?.enabled||false}
                    onChange={e => setEditForm({ ...editForm, recurrence:{...editForm.recurrence, enabled:e.target.checked} })}/>
                  Enable recurring task
                </label>
                {editForm.recurrence?.enabled && (
                  <select style={{ ...S.inp, marginTop:0 }} value={editForm.recurrence?.frequency||'weekly'}
                    onChange={e => setEditForm({ ...editForm, recurrence:{...editForm.recurrence, frequency:e.target.value} })}>
                    {['daily','weekly','biweekly','monthly'].map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ CHECKLIST TAB ══ */}
      {tab === 'checklist' && (
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0f172a' }}>Checklist</h3>
            {(task.checklist||[]).length > 0 && (
              <span style={{ fontSize:13, fontWeight:700, color:'#22c55e' }}>
                {Math.round((checkDone / (task.checklist||[]).length) * 100)}% complete
              </span>
            )}
          </div>
          {(task.checklist||[]).length > 0 && (
            <div style={{ height:6, background:'#f1f5f9', borderRadius:4, overflow:'hidden', marginBottom:20 }}>
              <div style={{ height:'100%', width:`${(checkDone/(task.checklist||[]).length)*100}%`, background:'linear-gradient(90deg,#22c55e,#16a34a)', borderRadius:4, transition:'width .4s' }}/>
            </div>
          )}

          {/* Blocking warning banner */}
          {(() => {
            const blocking = (task.checklist||[]).filter(item => {
              const aid = item.assignedTo?._id || item.assignedTo?.id || item.assignedToId;
              return aid && !item.completed;
            });
            return blocking.length > 0 ? (
              <div style={{ background:'#fffbeb', border:'1.5px solid #f59e0b', borderRadius:10, padding:'10px 14px',
                marginBottom:14, display:'flex', alignItems:'flex-start', gap:10 }}>
                <span style={{ fontSize:18, flexShrink:0 }}>🔒</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#92400e' }}>Task completion blocked</div>
                  <div style={{ fontSize:12, color:'#b45309', marginTop:2 }}>
                    {blocking.length} checklist item{blocking.length > 1 ? 's are' : ' is'} assigned to team members and must be approved by them before this task can be completed.
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {(task.checklist||[]).map(item => {
              const isOpen       = expandedCheck === item._id;
              const edits        = checkEdits[item._id] || { text: item.text, notes: item.notes || '', assignedToId: item.assignedTo?._id || item.assignedTo?.id || null };
              const isSaving     = savingCheck === item._id;
              const hasNotes     = item.notes && item.notes.trim();
              const assignedUser = item.assignedTo
                ? (typeof item.assignedTo === 'object' ? item.assignedTo : users.find(u => String(u._id||u.id) === String(item.assignedTo)))
                : null;
              // Picker: all non-client team members
              const pickerUsers = users.filter(u => u.role !== 'client');

              // Lock: item assigned to someone else → only they (or admin) can toggle
              const itemAssigneeId  = item.assignedTo?._id || item.assignedTo?.id || item.assignedToId;
              const isAssignedToMe  = !isAdmin && itemAssigneeId && String(itemAssigneeId) === String(user?._id || user?.id);
              const isLockedForMe   = !isAdmin && itemAssigneeId && !isAssignedToMe;
              const isWaiting       = isLockedForMe && !item.completed;

              return (
                <div key={item._id}
                  style={{ border:`1.5px solid ${isAssignedToMe && !item.completed ? '#6366f1' : isOpen ? '#6366f1' : '#f1f5f9'}`, borderRadius:12, overflow:'hidden',
                    transition:'border-color .2s', background: isAssignedToMe && !item.completed ? '#fafbff' : isOpen ? '#fafbff' : '#fff' }}>

                  {/* ── Row header ── */}
                  <div className="check-item"
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer' }}>

                    {/* Checkbox */}
                    <div
                      onClick={() => !isLockedForMe && toggleCheck(item._id, item.completed)}
                      title={isLockedForMe ? `Only ${assignedUser?.name || 'the assignee'} can check this off` : ''}
                      style={{ width:22, height:22, flexShrink:0,
                        border:`2px solid ${item.completed ? '#22c55e' : isWaiting ? '#f59e0b' : '#d1d5db'}`,
                        borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center',
                        background: item.completed ? '#22c55e' : isWaiting ? '#fffbeb' : '#fff',
                        cursor: isLockedForMe ? 'not-allowed' : 'pointer', transition:'all .2s' }}>
                      {item.completed && <FiCheck color="#fff" size={13}/>}
                      {isWaiting && !item.completed && <span style={{ fontSize:11 }}>🔒</span>}
                    </div>

                    {/* Title */}
                    <div style={{ flex:1 }}>
                      <span
                        onClick={() => !isLockedForMe && toggleCheck(item._id, item.completed)}
                        style={{ fontSize:14, color: item.completed ? '#94a3b8' : '#0f172a',
                          textDecoration: item.completed ? 'line-through' : 'none',
                          cursor: isLockedForMe ? 'default' : 'pointer', userSelect:'none' }}>
                        {item.text}
                      </span>
                      {isAssignedToMe && !item.completed && (
                        <div style={{ fontSize:11, color:'#6366f1', fontWeight:700, marginTop:2 }}>
                          📌 Assigned to you — mark complete when done
                        </div>
                      )}
                      {isAssignedToMe && item.completed && (
                        <div style={{ fontSize:11, color:'#22c55e', fontWeight:600, marginTop:2 }}>
                          ✅ You completed this
                        </div>
                      )}
                      {isWaiting && (
                        <div style={{ fontSize:11, color:'#d97706', fontWeight:600, marginTop:1 }}>
                          ⏳ Waiting for {assignedUser?.name || 'assignee'}
                        </div>
                      )}
                    </div>

                    {/* Notes indicator */}
                    {hasNotes && !isOpen && (
                      <span title="Has notes" style={{ color:'#6366f1', display:'flex', alignItems:'center' }}>
                        <FiFileText size={13}/>
                      </span>
                    )}

                    {/* Inline assign button — admin only for editing; non-admins see read-only badge */}
                    <div style={{ position:'relative', flexShrink:0 }}>
                      <button
                        onClick={e => { e.stopPropagation(); if (isAdmin) setAssignDropdown(assignDropdown === item._id ? null : item._id); }}
                        title={assignedUser ? `Assigned to ${assignedUser.name}${isAdmin ? ' — click to change' : ''}` : (isAdmin ? 'Assign to someone' : '')}
                        disabled={!isAdmin && !assignedUser}
                        style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 7px', borderRadius:14,
                          border:`1.5px solid ${assignedUser ? '#6366f1' : '#e2e8f0'}`,
                          background: assignedUser ? '#eff0ff' : '#f8fafc',
                          color: assignedUser ? '#6366f1' : '#94a3b8',
                          cursor:'pointer', fontSize:11, fontWeight:600, transition:'all .15s' }}>
                        {assignedUser ? (
                          <>
                            <span style={{ width:16, height:16, borderRadius:'50%', background:'#6366f1', color:'#fff',
                              display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800 }}>
                              {(assignedUser.name||'?').charAt(0).toUpperCase()}
                            </span>
                            {assignedUser.name.split(' ')[0]}
                          </>
                        ) : (
                          <>+ Assign</>
                        )}
                      </button>
                      {isAdmin && assignDropdown === item._id && (
                        <div style={{ position:'absolute', top:'calc(100% + 4px)', right:0, zIndex:200,
                          background:'#fff', border:'1px solid #e2e8f0', borderRadius:10,
                          boxShadow:'0 8px 24px rgba(0,0,0,.12)', minWidth:160, padding:6 }}
                          onClick={e => e.stopPropagation()}>
                          <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase',
                            letterSpacing:.5, padding:'2px 8px 6px' }}>Assign to</div>
                          <button
                            onClick={() => quickAssign(item._id, null)}
                            style={{ display:'block', width:'100%', textAlign:'left', padding:'6px 10px',
                              borderRadius:6, border:'none', background: !edits.assignedToId ? '#eff0ff' : 'none',
                              color: !edits.assignedToId ? '#6366f1' : '#475569',
                              cursor:'pointer', fontSize:12, fontWeight:600 }}>
                            None
                          </button>
                          {pickerUsers.map(u => {
                            const uid = String(u._id || u.id);
                            const sel = String(edits.assignedToId) === uid;
                            return (
                              <button key={uid}
                                onClick={() => quickAssign(item._id, uid)}
                                style={{ display:'flex', alignItems:'center', gap:7, width:'100%', textAlign:'left',
                                  padding:'6px 10px', borderRadius:6, border:'none',
                                  background: sel ? '#eff0ff' : 'none',
                                  color: sel ? '#6366f1' : '#475569',
                                  cursor:'pointer', fontSize:12, fontWeight:600 }}>
                                <span style={{ width:20, height:20, borderRadius:'50%', background: sel ? '#6366f1' : '#e2e8f0',
                                  color: sel ? '#fff' : '#64748b', display:'inline-flex', alignItems:'center',
                                  justifyContent:'center', fontSize:9, fontWeight:800, flexShrink:0 }}>
                                  {(u.name||'?').charAt(0).toUpperCase()}
                                </span>
                                {u.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Completed date */}
                    {item.completed && item.completedAt && (
                      <span style={{ fontSize:11, color:'#94a3b8', flexShrink:0 }}>
                        {new Date(item.completedAt).toLocaleDateString()}
                      </span>
                    )}

                    {/* Expand toggle */}
                    <button
                      onClick={() => toggleExpand(item)}
                      title={isOpen ? 'Collapse' : 'Add details'}
                      style={{ background:'none', border:'none', cursor:'pointer', color: isOpen ? '#6366f1' : '#94a3b8',
                        padding:'4px 5px', borderRadius:6, display:'flex', alignItems:'center', flexShrink:0, transition:'color .15s' }}>
                      {isOpen ? <FiChevronDown size={15}/> : <FiChevronRight size={15}/>}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={(e) => deleteCheck(e, item._id)}
                      title="Delete item"
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#cbd5e1', padding:'4px 6px', borderRadius:6,
                        display:'flex', alignItems:'center', flexShrink:0, transition:'color .15s' }}
                      onMouseEnter={e => e.currentTarget.style.color='#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color='#cbd5e1'}>
                      <FiTrash2 size={14}/>
                    </button>
                  </div>

                  {/* ── Accordion panel ── */}
                  {isOpen && (
                    <div style={{ borderTop:'1px solid #e8eaf0', padding:'14px 16px', background:'#f8faff' }}>
                      {/* Rename item */}
                      <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                        Item name
                      </label>
                      <input
                        value={edits.text}
                        onChange={e => setCheckEdits(prev => ({ ...prev, [item._id]: { ...edits, text: e.target.value } }))}
                        style={{ ...S.inp, marginTop:0, marginBottom:12, fontSize:14 }}
                        placeholder="Checklist item name"
                      />

                      {/* Notes */}
                      <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                        Notes / Details
                      </label>
                      <textarea
                        value={edits.notes}
                        onChange={e => setCheckEdits(prev => ({ ...prev, [item._id]: { ...edits, notes: e.target.value } }))}
                        placeholder="Add additional details, instructions, or context for this item…"
                        rows={3}
                        style={{ ...S.inp, marginTop:0, marginBottom:12, resize:'vertical', minHeight:80, fontSize:13, color:'#475569', lineHeight:1.7 }}
                      />

                      {/* Assign to */}
                      <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:.5, marginBottom:8 }}>
                        Assign to
                      </label>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
                        {/* Unassign option */}
                        <button
                          onClick={() => setCheckEdits(prev => ({ ...prev, [item._id]: { ...edits, assignedToId: null } }))}
                          style={{ padding:'5px 12px', borderRadius:20, border:`1.5px solid ${!edits.assignedToId ? '#6366f1' : '#e2e8f0'}`,
                            background: !edits.assignedToId ? '#eff0ff' : '#f8fafc',
                            color: !edits.assignedToId ? '#6366f1' : '#64748b',
                            cursor:'pointer', fontSize:12, fontWeight:600, transition:'all .15s' }}>
                          None
                        </button>
                        {pickerUsers.map(u => {
                          const uid  = String(u._id || u.id);
                          const sel  = String(edits.assignedToId) === uid;
                          return (
                            <button key={uid}
                              onClick={() => setCheckEdits(prev => ({ ...prev, [item._id]: { ...edits, assignedToId: uid } }))}
                              title={u.name}
                              style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:20,
                                border:`1.5px solid ${sel ? '#6366f1' : '#e2e8f0'}`,
                                background: sel ? '#eff0ff' : '#f8fafc',
                                color: sel ? '#6366f1' : '#475569',
                                cursor:'pointer', fontSize:12, fontWeight:600, transition:'all .15s' }}>
                              <span style={{ width:18, height:18, borderRadius:'50%', background: sel ? '#6366f1' : '#e2e8f0',
                                color: sel ? '#fff' : '#64748b', display:'inline-flex', alignItems:'center', justifyContent:'center',
                                fontSize:9, fontWeight:800 }}>
                                {(u.name||'?').charAt(0).toUpperCase()}
                              </span>
                              {u.name}
                            </button>
                          );
                        })}
                        {pickerUsers.length === 0 && (
                          <span style={{ fontSize:12, color:'#94a3b8', fontStyle:'italic' }}>No team members assigned to this task yet</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                        <button
                          style={S.btn('ghost')}
                          onClick={() => setExpandedCheck(null)}>
                          Cancel
                        </button>
                        <button
                          style={{ ...S.btn('primary'), opacity: isSaving ? .7 : 1 }}
                          disabled={isSaving}
                          onClick={() => saveCheckDetails(item._id)}>
                          <FiSave size={13}/> {isSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {(task.checklist||[]).length === 0 && (
            <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8' }}>
              <FiCheck size={32} style={{ marginBottom:10, opacity:.2 }}/><br/>
              <span style={{ fontSize:14 }}>No checklist items yet</span>
            </div>
          )}
          <div style={{ display:'flex', gap:10, marginTop:16, paddingTop:16, borderTop:'1px solid #f1f5f9' }}>
            <input value={newCheck} onChange={e => setNewCheck(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCheck()}
              placeholder="Add checklist item…" style={{ ...S.inp, marginTop:0, flex:1 }}/>
            <button style={S.btn('primary')} onClick={addCheck}><FiPlus size={14}/> Add</button>
          </div>
        </div>
      )}

      {/* ══ COMMENTS TAB ══ */}
      {tab === 'comments' && (
        <div style={S.card}>
          <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700, color:'#0f172a' }}>
            Comments <span style={{ fontWeight:500, color:'#94a3b8', fontSize:13 }}>({(task.comments||[]).length})</span>
          </h3>
          {(task.comments||[]).length === 0 && (
            <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8' }}>
              <span style={{ fontSize:28 }}>💬</span><br/>
              <span style={{ fontSize:14, marginTop:8, display:'block' }}>No comments yet. Start the conversation!</span>
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:20 }}>
            {(task.comments||[]).map((c, i) => (
              <div key={i} style={{ display:'flex', gap:12 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15 }}>
                  {(c.user?.name || c.author?.name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:10, marginBottom:6, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{c.user?.name || c.author?.name || 'Unknown'}</span>
                    <span style={{ fontSize:11, color:'#94a3b8' }}>
                      {new Date(c.createdAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </span>
                    {(c.mentions||[]).length > 0 && (
                      <span style={{ fontSize:11, background:'#ede9fe', color:'#7c3aed', padding:'2px 8px', borderRadius:8, fontWeight:600 }}>@ mention</span>
                    )}
                  </div>
                  <div style={{ background:'#f8fafc', borderRadius:12, padding:'10px 14px', fontSize:14, color:'#475569', lineHeight:1.7, border:'1px solid #f1f5f9' }}>
                    {c.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:16 }}>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8 }}>Press Enter to send · Use @name to mention someone</div>
            <div style={{ display:'flex', gap:10 }}>
              <input value={comment} onChange={e => setComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addComment()}
                placeholder="Write a comment…" style={{ ...S.inp, marginTop:0, flex:1 }}/>
              <button style={{ ...S.btn('primary'), opacity: commenting ? .6 : 1 }} onClick={addComment} disabled={commenting}>
                <FiSend size={13}/> {commenting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DEPENDENCIES TAB ══ */}
      {tab === 'dependencies' && (
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0f172a' }}>Dependencies</h3>
            {canEdit && (
              <button style={S.btn('primary')} onClick={() => { setDepModal(true); loadDeps(); }}>
                <FiLink size={13}/> Add Dependency
              </button>
            )}
          </div>
          {(task.dependencies||[]).length === 0
            ? <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8', fontSize:14 }}>
                <FiLink size={28} style={{ marginBottom:10, opacity:.2 }}/><br/>
                No dependencies — this task can start independently.
              </div>
            : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {(task.dependencies||[]).map(dep => {
                  const dc = SCOL[dep.status] || SCOL.todo;
                  return (
                    <div key={dep._id} className="dep-row"
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#f8fafc', borderRadius:12, border:'1.5px solid #f1f5f9', transition:'all .2s' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <Link to={`/tasks/${dep._id}`} style={{ fontWeight:700, fontSize:14, color:'#0f172a', textDecoration:'none' }}>{dep.title}</Link>
                        <div style={{ display:'flex', gap:8, marginTop:5 }}>
                          <span style={{ background: dc.bg, color: dc.color, padding:'2px 9px', borderRadius:8, fontSize:11, fontWeight:700 }}>{dep.status}</span>
                          {dep.dueDate && <span style={{ fontSize:11, color:'#94a3b8' }}>Due {new Date(dep.dueDate).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      {dep.status !== 'completed'
                        ? <span style={{ fontSize:11, background:'#fee2e2', color:'#991b1b', padding:'4px 10px', borderRadius:8, fontWeight:700 }}>🔒 Blocking</span>
                        : <span style={{ fontSize:11, background:'#d1fae5', color:'#065f46', padding:'4px 10px', borderRadius:8, fontWeight:700 }}>✓ Done</span>
                      }
                      {canEdit && <button onClick={() => removeDep(dep._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }}><FiX size={14}/></button>}
                    </div>
                  );
                })}
              </div>
          }
        </div>
      )}

      {/* ══ ACTIVITY TAB ══ */}
      {tab === 'activity' && (
        <div style={S.card}>
          <h3 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0f172a', display:'flex', alignItems:'center', gap:8 }}>
            <FiActivity size={15}/> Activity Log
          </h3>
          {(task.activity||[]).length === 0
            ? <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8', fontSize:14 }}>
                <FiActivity size={28} style={{ marginBottom:10, opacity:.2 }}/><br/>No activity logged yet.
              </div>
            : (task.activity||[]).slice().reverse().map((a, i) => (
                <div key={i} style={{ display:'flex', gap:12, padding:'12px 0', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                    {ACT_ICONS[a.action] || '•'}
                  </div>
                  <div style={{ flex:1 }}>
                    <span style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{a.user?.name || 'System'}</span>
                    {' '}<span style={{ fontSize:13, color:'#64748b' }}>{a.detail || a.action}</span>
                  </div>
                  <span style={{ fontSize:11, color:'#94a3b8', flexShrink:0, marginTop:2 }}>
                    {new Date(a.createdAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </span>
                </div>
              ))
          }
        </div>
      )}

      {/* ── Time Taken Modal ── */}
      {timeModal && (
        <div style={S.ov} onClick={e => e.target === e.currentTarget && setTimeModal(null)}>
          <div style={{ ...S.mo, maxWidth:360 }}>
            <h3 style={{ margin:'0 0 8px', fontSize:17, fontWeight:800, color:'#0f172a' }}>⏱ Time Taken</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#64748b' }}>How long did this checklist item take? (optional)</p>
            <input
              type="number" min="0" placeholder="Minutes (e.g. 30)"
              value={timeTaken}
              onChange={e => setTimeTaken(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitToggle(timeModal.iid, timeModal.done, timeTaken)}
              autoFocus
              style={{ width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:16 }}
            />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => submitToggle(timeModal.iid, timeModal.done, null)}
                style={{ padding:'8px 16px', borderRadius:9, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                Skip
              </button>
              <button onClick={() => submitToggle(timeModal.iid, timeModal.done, timeTaken)}
                style={{ padding:'8px 18px', borderRadius:9, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                Save & Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Dependency Modal ── */}
      {depModal && (
        <div style={S.ov} onClick={e => e.target === e.currentTarget && setDepModal(false)}>
          <div style={S.mo}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:17, fontWeight:800, color:'#0f172a' }}>Add Dependency</h3>
              <button style={{ background:'#f1f5f9', border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b' }} onClick={() => setDepModal(false)}><FiX/></button>
            </div>
            <p style={{ color:'#64748b', fontSize:13, margin:'0 0 14px', lineHeight:1.6 }}>This task will be blocked until the selected task completes:</p>
            <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:320, overflowY:'auto' }}>
              {allTasks.filter(t => !(task.dependencies||[]).some(d => (d._id||d) === t._id)).map(t => {
                const dc = SCOL[t.status] || SCOL.todo;
                return (
                  <div key={t._id} className="dep-row" onClick={() => addDep(t._id)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, border:'1.5px solid #f1f5f9', cursor:'pointer', transition:'all .15s' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background: PCOL[t.priority]||'#94a3b8', flexShrink:0 }}/>
                    <span style={{ flex:1, fontWeight:600, fontSize:13, color:'#0f172a' }}>{t.title}</span>
                    <span style={{ background: dc.bg, color: dc.color, padding:'2px 9px', borderRadius:8, fontSize:11, fontWeight:700 }}>{t.status}</span>
                  </div>
                );
              })}
              {allTasks.length === 0 && <div style={{ color:'#94a3b8', textAlign:'center', padding:24, fontSize:14 }}>No other tasks in this project</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteModal && (
        <div style={S.ov} onClick={e => e.target === e.currentTarget && setDeleteModal(false)}>
          <div style={{ ...S.mo, maxWidth:420, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🗑️</div>
            <h3 style={{ margin:'0 0 10px', fontSize:18, fontWeight:800, color:'#0f172a' }}>Delete Task?</h3>
            <p style={{ color:'#64748b', lineHeight:1.6, margin:'0 0 24px' }}>
              Permanently delete <b style={{ color:'#0f172a' }}>"{task.title}"</b>? This cannot be undone.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button style={S.btn('ghost')} onClick={() => setDeleteModal(false)}>Cancel</button>
              <button style={{ ...S.btn('danger'), background:'#ef4444', color:'#fff' }} onClick={deleteTask}>
                <FiTrash2 size={13}/> Delete Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
